const routes = {
  auth: {
    login: '/login',
    register: '/register',
    general: '/auth/general',
    plan: '/auth/plan',
    billing: '/auth/billing',
    setupClub: '/auth/setup-club',
    findClubs: '/auth/find-clubs',
    verifyEmail: '/api/auth/confirm-invitation',
    setPassword: '/set-password',
    forgotPassword: '/forgot-password',
    resetPassword: '/reset-password',
  },
  client: {
    home: '/',
  },
  public: {
    // The unauthenticated meet landing page, reached by the opaque short code (Meet.SignupCode), §6.1.
    meetSignup: {
      path: '/m/:code',
      url: (code) => `/m/${code}`,
    },
  },
  personal: {
    home: '/my',
    profile: '/my/profile',
    athletes: '/my/athletes',
    // Case 2 (§6.6) — a non-host-club portal user manages their own registrations for a meet inside their personal
    // portal. Same shared dashboard as the in-club §6.10 page, hosted in PersonalLayout instead of the club shell.
    meetRegistrations: {
      path: '/my/meets/:meetId/registrations',
      url: (meetId) => `/my/meets/${meetId}/registrations`,
    },
    // Case 3 (§6.6) — the guided onboarding wizard for a brand-new person who just created their account; reached only
    // via the dialog's onboard path. Ends with them a Visitor; from then on they manage via meetRegistrations (Case 2).
    meetOnboard: {
      path: '/my/onboard/:meetId',
      url: (meetId) => `/my/onboard/${meetId}`,
    },
  },
  admin: {
    startListsDemo: '/demo/start-lists',
    dashboard: '/dashboard',
    clubDashboard: {
      path: '/clubs/:clubId',
      url: (id) => {
        return `/clubs/${id}`;
      },
    },
    clubRecord: '/club-records',
    athleteDashboard: '/athlete-results',
    volunteerStatus: '/volunteer-status',
    addUser: {
      path: '/clubs/:id/users/add',
      url: (id) => {
        return `/clubs/${id}/users/add`;
      },
    },
    events: {
      path: '/clubs/:clubId/meets/:meetId/events/:viewType?',
      url: (clubId, meetId, viewType) => {
        return viewType
          ? `/clubs/${clubId}/meets/${meetId}/events/${viewType}`
          : `/clubs/${clubId}/meets/${meetId}/events`;
      },
    },
    addEvent: {
      path: '/clubs/:clubId/meets/:meetId/events/add',
      url: (clubId, meetId) => {
        return `/clubs/${clubId}/meets/${meetId}/events/add`;
      },
    },
    editEvent: {
      path: '/clubs/:clubId/meets/:meetId/events/edit',
      url: (clubId, meetId, eventId) => {
        return `/clubs/${clubId}/meets/${meetId}/events/edit`;
      },
    },
    results: {
      path: '/clubs/:clubId/meets/:meetId/results/:viewType/:eventType/:ageGroup/:gender/:roundType?',
      url: (
        clubId,
        meetId,
        viewType,
        eventType,
        ageGroup,
        gender,
        roundType = ''
      ) => {
        return `/clubs/${clubId}/meets/${meetId}/results/${viewType}/${eventType}/${ageGroup}/${gender}/${roundType}`;
      },
    },
    ageView: {
      path: '/clubs/:clubId/meets/:meetId/results/age-view/:eventType/:athleteAgeGroup/:gender/:roundType',
      url: (clubId, meetId, eventType, athleteAgeGroup, gender, roundType) =>
        `/clubs/${clubId}/meets/${meetId}/results/age-view/${encodeURIComponent(
          eventType
        )}/${athleteAgeGroup}/${gender}/${roundType}`,
    },
    eventRound: {
      path: '/clubs/:clubId/meets/:meetId/results/event-round/:eventType/:eventID',
      url: (clubId, meetId, eventType, eventID) =>
        `/clubs/${clubId}/meets/${meetId}/results/event-round/${encodeURIComponent(
          eventType
        )}/${eventID}`,
    },
    editResults: {
      path: '/clubs/:clubId/meets/:meetId/results/:viewType/:eventType/:ageGroup/:gender/:roundType/edit',
      url: (
        clubId,
        meetId,
        viewType,
        eventType,
        ageGroup,
        gender,
        roundType
      ) => {
        return `/clubs/${clubId}/meets/${meetId}/results/${viewType}/${eventType}/${ageGroup}/${gender}/${roundType}/edit`;
      },
    },
    meets: '/meets',
    addMeet: {
      path: '/clubs/:id/meets/add',
      url: (id) => {
        return `/clubs/${id}/meets/add`;
      },
    },
    showMeet: {
      path: '/clubs/:clubId/meets/:meetId/show',
      url: (clubId, meetId) => {
        return `/clubs/${clubId}/meets/${meetId}/show`;
      },
    },
    editMeet: {
      path: '/clubs/:clubId/meets/:meetId/edit',
      url: (clubId, meetId) => {
        return `/clubs/${clubId}/meets/${meetId}/edit`;
      },
    },
    meetGeneration: {
      path: '/clubs/:clubId/meets/:meetId/meet-generation',
      url: (clubId, meetId) => {
        return `/clubs/${clubId}/meets/${meetId}/meet-generation`;
      },
    },
    myMeetRegistrations: {
      path: '/clubs/:clubId/meets/:meetId/my-registrations',
      url: (clubId, meetId) => {
        return `/clubs/${clubId}/meets/${meetId}/my-registrations`;
      },
    },
    meetRegistrationImport: {
      path: '/meet-registration/:meetId/import',
      url: (meetId) => {
        return `/meet-registration/${meetId}/import`;
      },
    },
    meetStartLists: {
      path: '/clubs/:clubId/meets/:meetId/meet-start-lists',
      url: (clubId, meetId) => {
        return `/clubs/${clubId}/meets/${meetId}/meet-start-lists`;
      },
    },
    meetStartListDetails: {
      path: '/clubs/:clubId/meets/:meetId/start-list-details/:eventTypeCode/:ageGroup/:gender',
      url: (clubId, meetId, eventTypeCode, ageGroup, gender) => {
        return `/clubs/${clubId}/meets/${meetId}/start-list-details/${eventTypeCode}/${ageGroup}/${gender}`;
      },
    },
    meetStartListEditor: {
      path: '/clubs/:clubId/meets/:meetId/start-list-edit/:eventTypeCode/:ageGroup/:gender/:startListId',
      url: (clubId, meetId, eventTypeCode, ageGroup, gender, startListId) => {
        return `/clubs/${clubId}/meets/${meetId}/start-list-edit/${eventTypeCode}/${ageGroup}/${gender}/${startListId}`;
      },
    },
    trainingResults: {
      path: '/clubs/:clubId/meets/:meetId/training-results',
      url: (clubId, meetId) => {
        return `/clubs/${clubId}/meets/${meetId}/training-results`;
      },
    },
    meetPointsDashboard: {
      path: '/clubs/:clubId/meets/:meetId/meet-points',
      url: (clubId, meetId) => {
        return `/clubs/${clubId}/meets/${meetId}/meet-points`;
      },
    },
    members: {
      path: '',
      members: '/members',
      memberImport: '/members/import',
      addMember: {
        path: '/clubs/:id/members/add',
        url: (id) => {
          return `/clubs/${id}/members/add`;
        },
      },
      editMember: {
        path: '/clubs/:id/members/:email/edit',
        url: (id, email) => {
          return `/clubs/${id}/members/${email}/edit`;
        },
      },
      showMember: {
        path: '/clubs/:id/members/:email/show',
        url: (id, email) => {
          return `/clubs/${id}/members/${email}/show`;
        },
      },
      athletes: '/athletes',
      // A club's teams (school houses, factions). Only a club that has teams.
      teams: '/teams',
      athleteResults: {
        path: '/clubs/:clubId/athletes/:athleteId/results',
        url: (clubId, athleteId) => {
          return `/clubs/${clubId}/athletes/${athleteId}/results`;
        },
      },
      addAthlete: {
        path: '/clubs/:id/members/:email/athletes/add',
        url: (id, email) => {
          return `/clubs/${id}/members/${email}/athletes/add`;
        },
      },
      editAthlete: {
        path: '/clubs/:id/members/:email/athletes/:athleteId/edit',
        url: (id, email, athleteId) => {
          return `/clubs/${id}/members/${email}/athletes/${athleteId}/edit`;
        },
      },
    },
    guests: {
      path: '',
      members: '/guests/members',
      athletes: '/guests/athletes',
    },
    visitors: {
      path: '',
      members: '/visitors/members',
      athletes: '/visitors/athletes',
    },
    notification: '/notifications',
    profile: '/profile',
    // season: {
    //   path: '/season/:viewOption',
    //   url: (viewOption) => {
    //     return `/season/${viewOption}`;
    //   },
    // },
    reports: {
      path: '',
      athleteSeasonPoints: '/reports/athlete-season-points',
      seasonChampions: '/reports/season-champions',
      housePoints: '/reports/house-points',
      memberVolunteerPoints: '/reports/member-volunteer-points',
      athletePRs: '/reports/athlete-prs',
      eventParticipation: '/reports/event-participation',
      athleteAttendance: '/reports/athlete-attendance',
      mostMeetPRs: '/reports/most-meet-prs',
      seasonEventPerformance: '/reports/season-event-performance',
      resultsSync: '/reports/results-sync',
    },
    clubSettings: {
      path: '',
      general: '/club-settings',
      ageGroups: '/club-settings/age-groups',
      athleteGroups: '/club-settings/athlete-groups',
      volunteerRoles: '/club-settings/volunteer-roles',
      volunteerHoursTargets: '/club-settings/volunteer-hours-targets',
      eventTypes: '/club-settings/event-types',
      manageSeasons: {
        path: '/club-settings/season/:viewOption',
        url: (viewOption) => {
          return `/club-settings/season/${viewOption}`;
        },
      },
    },
    volunteerManagement: {
      path: '/clubs/:clubId/meets/:meetId/volunteers',
      url: (clubId, meetId) => {
        return `/clubs/${clubId}/meets/${meetId}/volunteers`;
      },
    },
    volunteerOpportunities: {
      path: '/clubs/volunteer-opportunities',
      url: () => {
        return `/clubs/volunteer-opportunities`;
      },
    },
  },
  notFound: '/404',
};

export default routes;
